import { Controller } from "@hotwired/stimulus"
import consumer from "../../channels/consumer"
import { DETACH_GRACE_MS } from "../../utils/notification-bell.util"

// Connects to data-controller="user--notifications-channel"
// Manages real-time notifications via ActionCable
//
// Usage:
//   <div data-controller="user--notifications-channel">
//     <div id="notification_counter">
//       <span id="notification_count">0</span>
//     </div>
//     <ul id="notifications_list"></ul>
//   </div>
//
// Listens for notification updates and updates the UI in real-time.
//
// ONE subscription for the page, shared by every bell that holds it: Action
// Cable keys a subscription by its identifier, so two objects for the same
// channel leave the second unconfirmed and both receive each message. A bell
// lets go a moment after it disconnects, so the next page's bell (or the same
// permanent bell, which every Turbo render detaches and re-inserts) takes the
// subscription over instead of resubscribing — and nothing broadcast in
// between is lost.
let subscription = null
let holders = 0

function hold() {
  holders += 1
  subscription ??= consumer.subscriptions.create("NotificationsChannel", { received })
}

function letGo() {
  holders -= 1
  if (holders > 0 || !subscription) return

  subscription.unsubscribe()
  subscription = null
}

function received(data) {
  const notificationCountElement = document.getElementById("notification_count")
  const notificationsListElement = document.getElementById("notifications_list")
  const targetElement = data.target ? document.getElementById(data.target) : null

  if (notificationCountElement && notificationsListElement) {
    // Increment the notification count
    const count = parseInt(notificationCountElement.innerHTML, 10) + 1
    notificationCountElement.innerHTML = count

    // Show the badge if it was hidden
    if (count > 0) {
      notificationCountElement.style.display = ""
    }

    // Insert the new notification data at the beginning of the list
    notificationsListElement.insertAdjacentHTML("afterbegin", data.notification)

    // If there's a target element and content, insert it there too
    if (targetElement && data.notification_content) {
      targetElement.insertAdjacentHTML("beforeend", data.notification_content)

      // Scroll to the newly added content
      const newElement = targetElement.lastElementChild
      if (newElement) {
        newElement.scrollIntoView({ behavior: "smooth", block: "start" })
      }
    }
  } else {
    console.error("Notification elements not found. Check your HTML structure.", {
      hasCountElement: !!notificationCountElement,
      hasListElement: !!notificationsListElement
    })
  }
}

export default class extends Controller {
  connect() {
    clearTimeout(this.detachTimer)
    this.detachTimer = undefined
    if (this.holding) return

    this.holding = true
    hold()
  }

  disconnect() {
    this.detachTimer = setTimeout(() => {
      this.detachTimer = undefined
      this.holding = false
      letGo()
    }, DETACH_GRACE_MS)
  }
}
